import React, { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

// Emoji icons for POS items/categories
const EMOJI_ICONS = [
  '🏠', '🏡', '🏢', '🏬', '🏗️', '🚗', '🛋️', '🧹', '🧴', '🧽', '🪣', '🪟',
  '🚿', '🛁', '🍳', '🫧', '💧', '❄️', '🔨', '🔧', '🛡️', '🩺', '💉', '💊',
  '🎁', '📦', '💨', '✨', '⭐', '🔥', '👷', '👨‍🔧', '✅', '📋', '🧪', '🩹',
  '🦵', '🟫', '💡', '🌿', '🌍', '🤝', '❤️', '🚀', '👥', '🏙️', '📊', '🗺️',
  '📱', '💻', '🖼️', '📝', '🎯', '💬', '🔔', '💎', '📅', '👶', '🦟', '🫁',
];

// Common Lucide icon list curated for CMS usage
const ICON_GROUPS = [
  {
    label: 'Umum',
    icons: ['Home', 'Settings', 'HelpCircle', 'Info', 'Search', 'Plus', 'Minus', 'X', 'Check', 'ChevronRight', 'ChevronDown', 'ChevronUp', 'ChevronLeft', 'Menu', 'MoreHorizontal', 'ExternalLink'],
  },
  {
    label: 'Bisnis & Konten',
    icons: ['Briefcase', 'Building2', 'Users', 'User', 'UserCheck', 'UserPlus', 'Phone', 'Mail', 'MessageCircle', 'MessageSquare', 'Calendar', 'Clock', 'MapPin', 'Globe', 'Star', 'Award', 'Trophy', 'Target', 'Shield', 'Zap', 'Leaf', 'Heart', 'ThumbsUp', 'Share2'],
  },
  {
    label: 'E-commerce & Produk',
    icons: ['Package', 'ShoppingCart', 'ShoppingBag', 'CreditCard', 'DollarSign', 'Banknote', 'Receipt', 'Tag', 'Percent', 'BadgeCheck', 'BadgePercent', 'Truck', 'Store'],
  },
  {
    label: 'Media & File',
    icons: ['Image', 'FileImage', 'Camera', 'Video', 'Music', 'FileText', 'File', 'Folder', 'Download', 'Upload', 'Paperclip', 'Link', 'Copy', 'Clipboard'],
  },
  {
    label: 'Tool & Aksi',
    icons: ['Wrench', 'Hammer', 'Pencil', 'Eraser', 'Trash2', 'Save', 'Printer', 'Eye', 'EyeOff', 'Lock', 'Unlock', 'Key', 'Filter', 'RefreshCw', 'RotateCcw', 'Play', 'Pause', 'Square'],
  },
  {
    label: 'Kesehatan & Medis',
    icons: ['HeartPulse', 'Stethoscope', 'Syringe', 'Pill', 'Thermometer', 'Activity', 'Lungs', 'Brain', 'Bone', 'Smile', 'Frown', 'Meh'],
  },
  {
    label: 'Alam & Lainnya',
    icons: ['Sun', 'Moon', 'Cloud', 'CloudRain', 'Snowflake', 'Flame', 'Droplets', 'Waves', 'Wind', 'Trees', 'Flower2', 'Mountain', 'Compass', 'Anchor', 'Rocket', 'Lightbulb', 'Gift'],
  },
];

const ALL_LUCIDE_ICONS = [...new Set(ICON_GROUPS.reduce((acc, g) => acc.concat(g.icons), []))];

function isEmoji(value) {
  if (!value) return false;
  // Check if value is a single emoji or emoji sequence
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Extended_Pictographic})+$/u;
  return emojiRegex.test(value) || EMOJI_ICONS.includes(value);
}

export default function IconPicker({ value = '', onChange, placeholder = 'Pilih icon...', label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('emoji');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmojis = search
    ? EMOJI_ICONS.filter(e => e.includes(search) || search.length <= 2)
    : null;

  const filteredLucideIcons = search
    ? ALL_LUCIDE_ICONS.filter(name => name.toLowerCase().includes(search.toLowerCase()))
    : null;

  const handleSelect = (iconValue) => {
    onChange(iconValue);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const renderLucideIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    if (!IconComponent) return <div className="w-5 h-5 bg-gray-200 rounded" />;
    return <IconComponent className="w-5 h-5" />;
  };

  const renderSelectedIcon = () => {
    if (!value) return <span className="flex-1 text-left text-gray-400">{placeholder}</span>;
    if (isEmoji(value)) {
      return <><span className="text-xl">{value}</span><span className="flex-1 text-left text-gray-700 text-xs">{value}</span></>;
    }
    return <><span className="text-gray-700">{renderLucideIcon(value)}</span><span className="flex-1 text-left text-gray-700">{value}</span></>;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white hover:border-primary/50 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
      >
        {renderSelectedIcon()}
        {value ? (
          <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600" title="Hapus">
            <X className="w-4 h-4" />
          </button>
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden" style={{ minWidth: 360 }}>
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari icon..."
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary outline-none"
                autoFocus
              />
            </div>
          </div>

          {!search && (
            <div className="flex border-b border-gray-100">
              <button type="button" onClick={() => setTab('emoji')} className={`flex-1 py-2 text-xs font-medium ${tab === 'emoji' ? 'text-brand border-b-2 border-brand' : 'text-gray-400'}`}>😊 Emoji</button>
              <button type="button" onClick={() => setTab('lucide')} className={`flex-1 py-2 text-xs font-medium ${tab === 'lucide' ? 'text-brand border-b-2 border-brand' : 'text-gray-400'}`}>🔷 Lucide</button>
            </div>
          )}

          <div className="max-h-56 overflow-y-auto p-2">
            {search ? (
              <div className="space-y-2">
                {filteredEmojis && filteredEmojis.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1 px-1">Emoji</div>
                    <div className="grid grid-cols-8 gap-1">
                      {filteredEmojis.map(e => (
                        <button key={e} type="button" onClick={() => handleSelect(e)}
                          className={`p-2 rounded-lg flex items-center justify-center text-xl hover:bg-gray-100 ${value === e ? 'bg-brand/10 ring-1 ring-brand' : ''}`}
                          title={e}>{e}</button>
                      ))}
                    </div>
                  </div>
                )}
                {filteredLucideIcons && filteredLucideIcons.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1 px-1">Lucide Icons</div>
                    <div className="grid grid-cols-6 gap-1">
                      {filteredLucideIcons.map(name => (
                        <button key={name} type="button" onClick={() => handleSelect(name)}
                          className={`p-2 rounded-lg flex flex-col items-center gap-1 hover:bg-primary/10 transition-colors ${value === name ? 'bg-primary/10 ring-1 ring-primary' : ''}`}
                          title={name}>
                          {renderLucideIcon(name)}
                          <span className="text-[10px] text-gray-500 truncate w-full text-center leading-tight">{name.length > 10 ? name.substring(0, 9) + '…' : name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {(!filteredEmojis || filteredEmojis.length === 0) && (!filteredLucideIcons || filteredLucideIcons.length === 0) && (
                  <div className="text-center py-4 text-sm text-gray-400">Tidak ditemukan</div>
                )}
              </div>
            ) : tab === 'emoji' ? (
              <div className="grid grid-cols-8 gap-1">
                {EMOJI_ICONS.map(e => (
                  <button key={e} type="button" onClick={() => handleSelect(e)}
                    className={`p-2 rounded-lg flex items-center justify-center text-2xl hover:bg-gray-100 transition-colors ${value === e ? 'bg-brand/10 ring-1 ring-brand' : ''}`}
                    title={e}>{e}</button>
                ))}
              </div>
            ) : (
              ICON_GROUPS.map(group => (
                <div key={group.label} className="mb-3">
                  <div className="text-xs text-gray-400 mb-1 px-1">{group.label}</div>
                  <div className="grid grid-cols-6 gap-1">
                    {group.icons.map(name => (
                      <button key={name} type="button" onClick={() => handleSelect(name)}
                        className={`p-2 rounded-lg flex flex-col items-center gap-1 hover:bg-primary/10 transition-colors ${value === name ? 'bg-primary/10 ring-1 ring-primary' : ''}`}
                        title={name}>
                        {renderLucideIcon(name)}
                        <span className="text-[10px] text-gray-500 truncate w-full text-center leading-tight">{name.length > 10 ? name.substring(0, 9) + '…' : name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function renderLucideIcon(iconName, className = 'w-5 h-5') {
  if (!iconName) return null;
  const IconComponent = LucideIcons[iconName];
  if (!IconComponent) return null;
  return <IconComponent className={className} />;
}